import type { FC } from 'react';
import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import type { Note } from '@flow/libs/data-validators/entity-schemas/contract';

import { AddNote } from '@app/components/contract-actions/add-note/add-note';
import { ContractNote } from '@app/components/contract-note';
import { T } from '@app/components/translation';
import agreements from '@app/reducers/entities/agreements';

import style from './contract-notes-list.module.scss';

type Props = {
  agreement: Oneflow.Agreement;
};

export const ContractNotesList: FC<Props> = ({ agreement }: Props) => {
  const dispatch = useDispatch();

  const removeState = useSelector((state) =>
    agreements.getRemoveContractNoteSelector(state, {
      id: agreement.id,
    }),
  );

  const resetRemoveState = useCallback(() => {
    dispatch(
      agreements.removeContractNoteReset({
        id: agreement.id,
      }),
    );
  }, [
    agreement.id,
    dispatch,
  ]);

  const fetchNotes = useCallback(() => {
    dispatch(
      agreements.fetchContractNotes({
        id: agreement.id,
      }),
    );
  }, [
    agreement.id,
    dispatch,
  ]);

  const removeNote = useCallback(
    (note: Note) =>
      dispatch(
        agreements.removeContractNote({
          id: agreement.id,
          data: {
            noteId: note.id,
          },
          pipe: {
            onSuccess: fetchNotes,
          },
        }),
      ),
    [
      agreement.id,
      dispatch,
      fetchNotes,
    ],
  );

  const renderNotes = () =>
    (agreement.notes as Note[])?.map((note) => {
      const { id } = note;
      const noteRemoveHandler = () => removeNote(note);

      return (
        <ContractNote
          key={id}
          note={note}
          removeState={removeState}
          removeNote={noteRemoveHandler}
          resetRemoveState={resetRemoveState}
        />
      );
    });

  return (
    <div className={style.NotesContainer}>
      <article className={style.NotesListHeader}>
        <T
          _str="Notes"
          _comment="Label for contract notes"
        />
      </article>
      <div className={style.NotesList}>{renderNotes()}</div>
      <div className={style.AddNoteButton}>
        <AddNote
          agreement={agreement}
          isRenderedInNoteList
        />
      </div>
    </div>
  );
};
